import axios from 'axios';
import { API_BASE_URL } from '../../../../constants';
import { refreshEntitlement } from '../../../../store/actions/entitlementRefresh';

// Joining or leaving a team can start or end the Premium a coach's paid plan
// includes, so the athlete's entitlement is fetched again after each change
// (in the background; it never throws). Every caller gets it: the onboarding
// wizards, the edit hub and the classic profile.
const teamsChanged = () => { refreshEntitlement(); };

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// The athlete's team steps keep their buttons busy until these answer, so a
// request that never answers (a stalled connection) gives up after 45
// seconds, longer than the 30 seconds the host lets any request run, with a
// message the step can show.
const STEP_LIMIT = { timeout: 45000, timeoutErrorMessage: 'The connection timed out. Try again.' };

export const fetchOnboardingCandidates = async ({ league, teamId, firstName, lastName }) => {
  const res = await axios.get(`${API_BASE_URL}/onboarding/candidates/`, {
    params: { league, teamId, firstName, lastName },
    headers: authHeaders(),
    ...STEP_LIMIT,
  });
  return res.data;
};

// The onboarding team step: an athlete's team and roster spot, or the team
// a coach asks to be verified on.
export const completeOnboarding = async (payload) => {
  const res = await axios.post(`${API_BASE_URL}/onboarding/complete/`, payload, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    ...STEP_LIMIT,
  });
  teamsChanged();
  return res.data;
};

// "Can't find my team": payload {teamName, sport, gender, level ('College',
// 'Pro', 'HS / Grassroots'), tier, ageGroup?, city, state, cascade,
// confirmedNoMatch?, cerebroProgramId?}. The server first checks the teams
// CACHE already has: any match answers 409 {error: 'possible_matches',
// matches} unless confirmedNoMatch. Else the request is recorded and an
// athlete's team becomes a provisional CACHE team on their profile:
// -> {message, requested, requestId, matchCheck, team?, teams?, fillNote?}.
export const requestTeam = async (payload) => {
  const res = await axios.post(`${API_BASE_URL}/onboarding/team-request/`, payload, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    ...STEP_LIMIT,
  });
  return res.data;
};

// Coach reads their own verification record. Returns:
//   { status: 'not_submitted' }  if no record yet
//   { status: 'pending' | 'verified' | 'rejected', verificationCode?, ... }
export const fetchMyCoachStatus = async () => {
  const res = await axios.get(`${API_BASE_URL}/coaches/me/`, {
    headers: authHeaders(),
  });
  return res.data;
};

// What a coach code would link (team, coach) before the athlete confirms.
export const previewCoachCode = async (code) => {
  const res = await axios.get(`${API_BASE_URL}/athletes/coach-code/preview/`, {
    params: { code },
    headers: authHeaders(),
    ...STEP_LIMIT,
  });
  return res.data;
};

// Drop one verified team (the coach verification only; the athlete's own
// team selection stays). Pass teamId, or teamName for a legacy link.
export const removeVerifiedTeam = async ({ teamId, teamName }) => {
  const res = await axios.post(
    `${API_BASE_URL}/athletes/coach-code/remove/`,
    teamId ? { teamId } : { teamName },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  teamsChanged();
  return res.data;
};

// Athlete enters a coach code: their first team is inherited, a different
// team is added beside it (school team + travel team). `teamId` picks one
// of the coach's teams when the coach covers several, and `league` says
// which squad: the 17U, 16U and 15U squads of a program share its id.
export const linkCoachCode = async (code, teamId, league) => {
  const res = await axios.post(
    `${API_BASE_URL}/athletes/link-coach-code/`,
    { code, ...(teamId ? { teamId } : {}), ...(teamId && league ? { league } : {}) },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, ...STEP_LIMIT },
  );
  teamsChanged();
  return res.data;
};

// Admin endpoints — list pending coach verifications + verify / reject.
export const fetchPendingCoaches = async (statusFilter = 'pending') => {
  const res = await axios.get(`${API_BASE_URL}/admin/coaches/pending/`, {
    params: { status: statusFilter },
    headers: authHeaders(),
  });
  return res.data;
};

export const verifyCoach = async (email) => {
  const res = await axios.post(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/verify/`,
    {},
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data;
};

// Admin issues a fresh verification code for a coach (and verifies them if
// they weren't already). Used by the Manage teams dialog for coaches that
// were verified via team assignment and never got a code, or to rotate one.
export const adminGenerateCoachCode = async (email) => {
  const res = await axios.post(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/generate-code/`,
    {},
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data;
};

// Coach asserts a CACHE-registered roster player really is one of theirs.
// Flips that athlete's status to verified and links them to the coach's
// team. 403 if the coach isn't verified themselves. `team` ({teamId,
// league}) is the squad whose roster the coach is looking at: the server
// verifies the athlete on that squad when it is the coach's (else on the
// squad the athlete already plays for), so an athlete is never moved to
// another age group of the program (10/19 notes, M-CODES 1.3c).
export const coachVerifyAthlete = async (athleteId, team = null) => {
  const body = team?.teamId ? { teamId: String(team.teamId), league: team.league || '' } : {};
  const res = await axios.post(
    `${API_BASE_URL}/coach/verify-athlete/${encodeURIComponent(athleteId)}/`,
    body,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data || {};
};

// Coach: live Cerebro roster for the coach's verified team. Each row is
// flagged `registered: true` (with a `cacheAthlete` deep-link payload)
// when the player has a CACHE account. Pass `teamId` to scope to a
// specific team (admin-assigned additional team); defaults to primary.
// When the live feed cannot answer, the saved roster comes back with
// `rosterSavedAt` (ISO): a view shows "Roster saved <date>", never the
// provider's name. (No page reads this endpoint today: the Coach Mode
// pages read coach/dashboard/, whose rosterSnapshot shows SavedRosterNotice.)
export const fetchCoachTeamRoster = async (teamId, coachEmail = null, opts = {}) => {
  const params = {};
  if (teamId) params.teamId = teamId;
  // Staff-only: scope the roster to another coach (admin "Team Players" tab).
  if (coachEmail) params.coachEmail = coachEmail;
  // League admins open teams not in their explicit list — pass the team's
  // league key (+ name) so the backend can authorize + load it.
  if (opts.league) params.league = opts.league;
  if (opts.teamName) params.teamName = opts.teamName;
  // Optional past-season selection (year). Omit for the current season.
  if (opts.season) params.season = opts.season;
  const res = await axios.get(`${API_BASE_URL}/coach/team-roster/`, {
    headers: authHeaders(),
    params,
  });
  return res.data || {};
};

// Coach: list of all teams the signed-in coach can access (primary +
// additional). Used to render the team selector on the team-players
// page and CacheShare.
export const fetchCoachMyTeams = async () => {
  const res = await axios.get(`${API_BASE_URL}/coach/teams/`, {
    headers: authHeaders(),
  });
  return res.data?.teams || [];
};

// Admin: assign an additional team to a coach.
export const adminAddCoachTeam = async (email, payload) => {
  const res = await axios.post(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/teams/`,
    payload,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data || {};
};

// Admin: force-change (or clear) an athlete's team. Pass {clear: true} to
// remove the team, or the resolved team payload to set it.
export const adminAssignAthleteTeam = async (email, payload) => {
  const res = await axios.post(
    `${API_BASE_URL}/admin/athletes/${encodeURIComponent(email)}/assign-team/`,
    payload,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data || {};
};

// Admin: grant league-admin access to a coach account (sees every team in the
// league across the dashboard + CacheShare). payload: {label, cerebroSports}.
export const adminSetLeagueAdmin = async (email, payload) => {
  const res = await axios.post(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/league-admin/`,
    payload,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data || {};
};

// Admin: revoke a coach's league-admin grant.
export const adminClearLeagueAdmin = async (email) => {
  const res = await axios.delete(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/league-admin/`,
    { headers: authHeaders() },
  );
  return res.data || {};
};

// Admin: remove an additional team from a coach. The squads of a program
// share its id, so `league` names the squad (only that squad goes).
export const adminRemoveCoachTeam = async (email, teamId, league = '') => {
  const res = await axios.delete(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/teams/${encodeURIComponent(teamId)}/`,
    { headers: authHeaders(), ...(league ? { params: { league } } : {}) },
  );
  return res.data || {};
};

// Coach CacheShare session — restore + persist the coach's working state
// (revenue amount + imported players) across visits.
export const fetchCoachCacheShareSession = async () => {
  const res = await axios.get(`${API_BASE_URL}/coach/cacheshare/session/`, {
    headers: authHeaders(),
  });
  return res.data || {};
};

export const saveCoachCacheShareSession = async (payload) => {
  const res = await axios.put(
    `${API_BASE_URL}/coach/cacheshare/session/`,
    payload,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data || {};
};

export const unverifyCoach = async (email) => {
  const res = await axios.post(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/unverify/`,
    {},
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data;
};

export const rejectCoach = async (email) => {
  const res = await axios.post(
    `${API_BASE_URL}/admin/coaches/${encodeURIComponent(email)}/reject/`,
    {},
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return res.data;
};
